import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Send, Lock, X } from 'lucide-react'
import { teacherChatApi } from '@/services/api/teacherChat'
import { useToast } from '@/components/ui/ToastProvider'
import { ScopeSelector } from './ScopeSelector'
import { lessonShortLabel } from './scopeUtils'
import type { ScopeLessonInfo } from '@/types/teacher-chat'

interface ChatInputProps {
  requestId: string
  isReadOnly: boolean
  lessons: ScopeLessonInfo[]
  /** Aktive Filter-Lektion-IDs fuer Smart-Vererbung (DD6); null = kein Filter. */
  activeScopeFilterIds: string[] | null
  filterKey: string
}

export function ChatInput({
  requestId,
  isReadOnly,
  lessons,
  activeScopeFilterIds,
  filterKey,
}: ChatInputProps) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([])

  // Smart-Vererbung (DD6): bei aktivem Filter prefillen; bei Wechsel re-syncen.
  useEffect(() => {
    setSelectedLessonIds(activeScopeFilterIds ? [...activeScopeFilterIds] : [])
  }, [filterKey, activeScopeFilterIds])

  const handleSend = async () => {
    if (!message.trim() || sending) return
    setSending(true)
    try {
      await teacherChatApi.sendMessage(
        requestId,
        message.trim(),
        selectedLessonIds.length ? selectedLessonIds : undefined
      )
      setMessage('')
      await queryClient.invalidateQueries({ queryKey: ['chat-messages', requestId] })
      await queryClient.invalidateQueries({ queryKey: ['teacher-chats'] })
    } catch (e: unknown) {
      const detail = (e as { detail?: string })?.detail || 'Nachricht konnte nicht gesendet werden'
      showToast({ message: detail, type: 'error', duration: 5000 })
    } finally {
      setSending(false)
    }
  }

  if (isReadOnly) {
    return (
      <div className="p-4 border-t border-gray-200 bg-gray-50 shrink-0">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
          <Lock className="w-4 h-4" />
          Dieser Chat ist archiviert (read-only)
        </div>
      </div>
    )
  }

  const selectedLessons = lessons.filter(l => selectedLessonIds.includes(l.lesson_request_id))

  return (
    <div className="p-4 border-t border-gray-200 bg-white shrink-0 min-w-0">
      {lessons.length > 0 && (
        <div className="mb-2 flex items-center gap-1.5 flex-wrap">
          {selectedLessons.length > 0 && (
            <>
              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">
                Betrifft:
              </span>
              {selectedLessons.map(l => (
                <span
                  key={l.lesson_request_id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-indigo-50 text-indigo-600 border border-indigo-100"
                >
                  {lessonShortLabel(l)}
                  <button
                    type="button"
                    onClick={() => setSelectedLessonIds(prev => prev.filter(x => x !== l.lesson_request_id))}
                    className="hover:text-indigo-800"
                    aria-label="Lektion entfernen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </>
          )}
          <div className="ml-auto">
            <ScopeSelector
              lessons={lessons}
              selectedIds={selectedLessonIds}
              onChange={setSelectedLessonIds}
              disabled={sending}
            />
          </div>
        </div>
      )}
      <div className="flex gap-2 min-w-0">
        <input
          type="text"
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSend() }}
          placeholder={selectedLessonIds.length ? 'Nachricht zu ausgewählten Lektionen...' : 'Nachricht an alle schreiben...'}
          className="flex-1 min-w-0 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={sending}
          aria-label="Nachricht eingeben"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !message.trim()}
          className="px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-medium transition-colors flex items-center gap-1.5 shrink-0"
          aria-label="Nachricht senden"
        >
          <Send className="w-4 h-4" />
          Senden
        </button>
      </div>
    </div>
  )
}
